import { useCallback, useEffect } from 'react';
import useUrlViewState from './useUrlViewState';

/**
 * A short, whole collection shown a page at a time, in the browser.
 *
 * Partners and operators are read from DynamoDB in one go and cached whole —
 * the API has no page to hand out, and slicing an already-loaded array in the
 * Lambda would save nothing while putting a network round trip behind every
 * keystroke of the search. So the screen keeps the list and pages it here,
 * and the reader gets instant filtering over the whole collection rather than
 * a search of the page they happen to be on. The moment a list is long enough
 * that fetching it whole is the problem, this is the wrong hook and the
 * endpoint needs `page`/`pageSize` the way `/bookings` has them.
 *
 * The view — what is searched for, which filters are on, which page, how many
 * rows — goes in the URL rather than in useState, because React Router
 * unmounts the screen the moment the reader opens something else and useState
 * does not survive that. See `useUrlViewState`.
 *
 * @param items    the whole collection, or null/undefined before it lands
 * @param matches  `(item, view) => boolean`: the screen's own search/filters
 * @param loaded   whether the collection has actually arrived (see below)
 * @param defaults extra view params this screen keeps, on top of the three
 *                 every paged list has; the TYPE of each is its codec
 */

export const DEFAULT_PAGE_SIZE = 25;

/** The three params every paged list screen keeps, whatever else it adds. */
export const PAGED_LIST_VIEW = { search: '', page: 1, size: DEFAULT_PAGE_SIZE };

export default function usePagedList({
  items, matches, loaded = true, defaults,
}) {
  const { view, setView } = useUrlViewState({ ...PAGED_LIST_VIEW, ...defaults });
  const { page } = view;
  // `?size=0` is a hand-edited URL, not a request for an infinitely long page.
  const size = Math.max(1, view.size);

  // 158 partners filtered on every keystroke is nothing, and memoising it
  // would mean pinning `matches` in a ref to stop an inline arrow busting the
  // cache on every render. Explicit beats clever at this size.
  const all = items || [];
  const filtered = matches ? all.filter((item) => matches(item, view)) : all;

  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / size));

  // A view can outrun its results: a link shared from page 7, or a filter
  // narrowed in another tab. Show the last page that exists rather than an
  // empty table with the pager insisting there is more.
  const safePage = Math.min(Math.max(page, 1), pages);
  const rows = filtered.slice((safePage - 1) * size, safePage * size);

  /**
   * ...and put the URL right, but ONLY once the collection has arrived. An
   * empty list mid-fetch is not evidence that page 3 is out of range, and
   * rewriting it there would throw away the page a reader had just come Back
   * to — the exact loss the URL is here to prevent.
   */
  useEffect(() => {
    if (loaded && page !== safePage) setView({ page: safePage });
  }, [loaded, page, safePage, setView]);

  /**
   * Turning a page is a deliberate move to a new page, so it starts at the
   * top — the pager sits under the table, and without this the reader lands
   * at the bottom of the new page looking at its last row.
   */
  const setPage = useCallback((next) => {
    setView({ page: next });
    window.scrollTo(0, 0);
  }, [setView]);

  const setSize = useCallback((next) => setView({ size: next, page: 1 }), [setView]);

  /**
   * Any change to WHAT is being looked at starts again at page one. Filtering
   * 158 partners down to 3 while sitting on page 4 would otherwise show an
   * empty table, which reads as "no results" and is a lie.
   */
  const setFilter = useCallback((patch) => setView({ ...patch, page: 1 }), [setView]);

  return {
    view,
    rows,
    total,
    setFilter,
    setPage,
    setSize,
    // The shape ListPager takes, which is the API's own envelope.
    pagination: {
      page: safePage, limit: size, total, pages,
    },
  };
}
